import React, { useState, useCallback } from 'react';
import TradingRulesSection from './TradingRulesSection';
import { createEmptyRuleGroup, createIndicatorCondition, createTrendLineCondition, createCompositeCondition, createCandlestickCondition, createVoteCondition, createChainCondition } from '../../../utils/strategyUtils';
import IndicatorConditionBuilder from '../../common/Builders/IndicatorConditionBuilder';
import TrendLineConditionBuilder from '../../common/Builders/TrendLineConditionBuilder';
import CompositeConditionBuilder from '../../common/Builders/CompositeConditionBuilder';
import CandlestickConditionBuilder from '../../common/Builders/CandlestickConditionBuilder';
import VoteConditionBuilder from '../../common/Builders/VoteConditionBuilder';
import ChainConditionBuilder from '../../common/Builders/ChainConditionBuilder';
import TrendLineChartModal from '../CreateStrategy/TrendLineChartModal';
import BasicInfoStep from '../CreateStrategy/steps/BasicInfoStep';
import RiskManagementStep from '../CreateStrategy/steps/RiskManagementStep';
import RuleLibraryBrowser from '../../common/Builders/RuleLibraryBrowser';
import RuleTemplateParameterForm from '../../common/Builders/RuleTemplateParameterForm';
import RuleSaveDialog from '../../common/Builders/RuleSaveDialog';
import { ruleLibraryService } from '../../../config/api';

// SVG Icons
const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const SaveIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
    </svg>
);

const AddIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
);

/**
 * StrategyBuilder - Main component for building strategies using Sets
 */
const StrategyBuilder = ({
    formData,
    onChange,
    onSave,
    onCancel,
    loading = false,
    errors = {},
    availableTrendLines = [],
    rulesSchema = null,
    indicatorSchemas = null,
}) => {
    const [conditionDialog, setConditionDialog] = useState({
        open: false,
        groupIndex: null,
        conditionIndex: null, // null for new
        conditionType: null, // 'INDICATOR', 'COMPOSITE', 'TRENDLINE', 'LIBRARY'
        condition: null,
        view: 'BUILD', // 'BUILD', 'LIBRARY', 'CUSTOMIZE', 'SAVE'
        selectedTemplate: null,
    });

    const [trendLineModal, setTrendLineModal] = useState({
        open: false,
        // When opening from condition builder, we might need context. 
        // But TrendLineConditionBuilder handles parameters inline usually.
        // If we want a separate flow where "Add Trendline Rule" opens chart modal first:
        callback: null
    });

    // --- Handlers for Rule Groups ---
    const handleAddGroup = (action) => {
        const entryCount = (formData.ruleGroups || []).filter(g => g.action === 'BUY').length;
        const exitCount = (formData.ruleGroups || []).filter(g => g.action === 'SELL').length;
        const groupNum = action === 'BUY' ? entryCount + 1 : exitCount + 1;
        const newGroup = createEmptyRuleGroup(action, `${action === 'BUY' ? 'Entry' : 'Exit'} Group ${groupNum}`);

        onChange({
            ...formData,
            ruleGroups: [...(formData.ruleGroups || []), newGroup],
        });
    };

    const handleDeleteGroup = (groupIndex) => {
        const newGroups = [...(formData.ruleGroups || [])];
        newGroups.splice(groupIndex, 1);
        onChange({
            ...formData,
            ruleGroups: newGroups,
        });
    };

    const handleDuplicateGroup = (groupIndex) => {
        const group = formData.ruleGroups[groupIndex];
        const newGroup = {
            ...group,
            groupId: `group-${Date.now()}`,
            groupName: `${group.groupName || 'Group'} (Copy)`,
            conditions: [...(group.conditions || [])]
        };

        const newGroups = [...(formData.ruleGroups || [])];
        newGroups.splice(groupIndex + 1, 0, newGroup);

        onChange({
            ...formData,
            ruleGroups: newGroups,
        });
    };

    const handleUpdateGroup = (groupIndex, updatedGroup) => {
        const newGroups = [...(formData.ruleGroups || [])];
        newGroups[groupIndex] = updatedGroup;
        onChange({
            ...formData,
            ruleGroups: newGroups,
        });
    };

    // --- Handlers for Basic Info ---
    const handleBasicInfoChange = (field, value) => {
        onChange({
            ...formData,
            [field]: value,
        });
    };

    // --- Condition Dialog Handlers ---
    const openConditionDialog = (groupIndex, conditionType, conditionIndex = null) => {
        let condition = null;

        if (conditionIndex === null) {
            // New condition
            condition = conditionType === 'INDICATOR'
                ? createIndicatorCondition()
                : conditionType === 'COMPOSITE'
                    ? createCompositeCondition()
                    : conditionType === 'TRENDLINE'
                        ? createTrendLineCondition()
                        : conditionType === 'CANDLESTICK'
                            ? createCandlestickCondition()
                            : conditionType === 'VOTE'
                                ? createVoteCondition()
                                : conditionType === 'CHAIN'
                                    ? createChainCondition()
                                    : null;
        } else {
            // Editing existing
            condition = { ...formData.ruleGroups[groupIndex].conditions[conditionIndex] };
        }

        setConditionDialog({
            open: true,
            groupIndex,
            conditionIndex,
            conditionType,
            condition,
            view: conditionType === 'LIBRARY' ? 'LIBRARY' : 'BUILD',
            selectedTemplate: null,
        });
    };

    const handleSaveCondition = () => {
        const { groupIndex, conditionIndex, condition } = conditionDialog;
        const group = formData.ruleGroups[groupIndex];
        const newConditions = [...group.conditions];

        if (conditionIndex === null) {
            newConditions.push(condition);
        } else {
            newConditions[conditionIndex] = condition;
        }

        const updatedGroup = { ...group, conditions: newConditions };
        handleUpdateGroup(groupIndex, updatedGroup);
        closeConditionDialog();
    };

    const handleDeleteCondition = (groupIndex, conditionIndex) => {
        const group = formData.ruleGroups[groupIndex];
        const newConditions = group.conditions.filter((_, i) => i !== conditionIndex);
        const updatedGroup = { ...group, conditions: newConditions };
        handleUpdateGroup(groupIndex, updatedGroup);
    };

    const closeConditionDialog = () => {
        setConditionDialog({
            open: false,
            setIndex: null,
            groupType: null,
            conditionIndex: null,
            conditionType: null,
            condition: null,
            view: 'BUILD',
            selectedTemplate: null,
        });
    };

    const handleDialogConditionChange = (updatedCondition) => {
        setConditionDialog(prev => ({
            ...prev,
            condition: updatedCondition
        }));
    };

    // --- Rule Library Handlers ---
    const handleSelectTemplate = (template) => {
        if (template.parameterSchemas && template.parameterSchemas.length > 0) {
            setConditionDialog(prev => ({
                ...prev,
                view: 'CUSTOMIZE',
                selectedTemplate: template
            }));
        } else {
            // No parameters to customize, add immediately
            const { groupIndex } = conditionDialog;
            const group = formData.ruleGroups[groupIndex];
            const newConditions = [...group.conditions, template.ruleDefinition];
            const updatedGroup = { ...group, conditions: newConditions };
            handleUpdateGroup(groupIndex, updatedGroup);
            closeConditionDialog();
        }
    };

    const handleTemplateCustomizationComplete = (customizedCondition) => {
        const { groupIndex } = conditionDialog;
        const group = formData.ruleGroups[groupIndex];
        const newConditions = [...group.conditions, customizedCondition];
        const updatedGroup = { ...group, conditions: newConditions };
        handleUpdateGroup(groupIndex, updatedGroup);
        closeConditionDialog();
    };

    const handleSaveToLibraryClick = () => {
        setConditionDialog(prev => ({
            ...prev,
            view: 'SAVE'
        }));
    };

    const handleRuleSaved = (savedRule) => {
        setConditionDialog(prev => ({
            ...prev,
            view: 'BUILD'
        }));
    };

    // --- Trendline Modal Handlers ---
    // This is used for "Draw/Select Trendline" flow specifically
    const openTrendLineModal = (callback) => {
        setTrendLineModal({
            open: true,
            callback
        });
    };

    const handleTrendLineSelection = (trendLine) => {
        if (trendLineModal.callback) {
            trendLineModal.callback(trendLine);
        }
    };

    // Note: TrendLineConditionBuilder handles its own modal logic

    return (
        <div className="flex flex-col h-full">
            {/* Actions Toolbar */}
            <div className="flex items-center justify-end px-6 py-4 border-b border-base-200 shrink-0">
                <div className="flex gap-2">
                    <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={loading}>Cancel</button>
                    <button className="btn btn-primary btn-sm gap-2" onClick={onSave} disabled={loading}>
                        {loading ? <span className="loading loading-spinner loading-xs"></span> : <SaveIcon />}
                        Save Strategy
                    </button>
                </div>
            </div>

            {/* Combined Scrollable Content */}
            <div className="flex-1 overflow-auto p-6">

                {/* Basic Info Section */}
                <div className="mb-8">
                    <BasicInfoStep
                        data={formData}
                        onChange={handleBasicInfoChange}
                        errors={errors}
                        compact={true}
                    />
                </div>

                {/* Risk Management Section */}
                <div className="mb-8">
                    <RiskManagementStep
                        data={formData}
                        onChange={handleBasicInfoChange}
                        errors={errors}
                        compact={false}
                    />
                </div>

                {/* Trading Rules Section */}
                <div className="mb-8">
                    <TradingRulesSection
                        ruleGroups={formData.ruleGroups || []}
                        onUpdateGroup={handleUpdateGroup}
                        onAddGroup={handleAddGroup}
                        onDeleteGroup={handleDeleteGroup}
                        onDuplicateGroup={handleDuplicateGroup}
                        onAddCondition={(groupIndex, type) => openConditionDialog(groupIndex, type)}
                        onEditCondition={(groupIndex, cIndex) => {
                            const cond = formData.ruleGroups[groupIndex].conditions[cIndex];
                            openConditionDialog(groupIndex, cond.conditionType, cIndex);
                        }}
                        onDeleteCondition={handleDeleteCondition}
                        disabled={loading}
                        rulesSchema={rulesSchema}
                    />
                </div>

                {/* Bottom Actions Toolbar */}
                <div className="flex items-center justify-end px-2 pt-4 border-t border-base-200 mt-4">
                    <div className="flex gap-2">
                        <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={loading}>Cancel</button>
                        <button className="btn btn-primary btn-sm gap-2" onClick={onSave} disabled={loading}>
                            {loading ? <span className="loading loading-spinner loading-xs"></span> : <SaveIcon />}
                            Save Strategy
                        </button>
                    </div>
                </div>
            </div>

            {/* Error Alert (Floating or Fixed) */}
            {
                Object.keys(errors).length > 0 && (
                    <div className="toast toast-end z-50">
                        <div className="alert alert-error text-sm">
                            <span>Please fix the errors before saving.</span>
                        </div>
                    </div>
                )
            }

            {/* Condition Dialog */}
            <dialog className={`modal ${conditionDialog.open ? 'modal-open' : ''}`}>
                <div className="modal-box max-w-4xl">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold text-lg">
                            {conditionDialog.view === 'LIBRARY' ? 'Browse Rule Library' :
                                conditionDialog.view === 'CUSTOMIZE' ? 'Customize Parameters' :
                                    conditionDialog.view === 'SAVE' ? 'Save to Library' :
                                        <>
                                            {conditionDialog.conditionIndex === null ? 'Add' : 'Edit'}{' '}
                                            {conditionDialog.condition?.conditionType === 'INDICATOR' ? 'Indicator' :
                                                conditionDialog.condition?.conditionType === 'COMPOSITE' ? 'Composite' :
                                                    conditionDialog.condition?.conditionType === 'CANDLESTICK' ? 'Candlestick Pattern' :
                                                        conditionDialog.condition?.conditionType === 'VOTE' ? 'Vote (N-of-M)' :
                                                            conditionDialog.condition?.conditionType === 'CHAIN' ? 'Chain (Sequential)' : 'Trend Line'} Condition
                                        </>
                            }
                        </h3>
                        <button className="btn btn-sm btn-circle btn-ghost" onClick={closeConditionDialog}>
                            <CloseIcon />
                        </button>
                    </div>

                    <div className="divider my-2"></div>

                    <div className="py-2">
                        {conditionDialog.view === 'LIBRARY' ? (
                            <RuleLibraryBrowser
                                onSelect={handleSelectTemplate}
                                onCancel={closeConditionDialog}
                            />
                        ) : conditionDialog.view === 'CUSTOMIZE' ? (
                            <RuleTemplateParameterForm
                                template={conditionDialog.selectedTemplate}
                                onComplete={handleTemplateCustomizationComplete}
                                onCancel={() => setConditionDialog(prev => ({ ...prev, view: 'LIBRARY' }))}
                            />
                        ) : conditionDialog.view === 'SAVE' ? (
                            <RuleSaveDialog
                                ruleDefinition={conditionDialog.condition}
                                onSave={handleRuleSaved}
                                onCancel={() => setConditionDialog(prev => ({ ...prev, view: 'BUILD' }))}
                            />
                        ) : (
                            <>
                                {conditionDialog.condition?.conditionType === 'INDICATOR' ? (
                                    <IndicatorConditionBuilder
                                        condition={conditionDialog.condition}
                                        onChange={handleDialogConditionChange}
                                        errors={{}} // Pass errors if validation exists
                                        disabled={loading}
                                        rulesSchema={rulesSchema}
                                        indicatorSchemas={indicatorSchemas}
                                    />
                                ) : conditionDialog.condition?.conditionType === 'COMPOSITE' ? (
                                    <CompositeConditionBuilder
                                        condition={conditionDialog.condition}
                                        onChange={handleDialogConditionChange}
                                        errors={{}}
                                        disabled={loading}
                                        rulesSchema={rulesSchema}
                                        indicatorSchemas={indicatorSchemas}
                                    />
                                ) : conditionDialog.condition?.conditionType === 'CANDLESTICK' ? (
                                    <CandlestickConditionBuilder
                                        condition={conditionDialog.condition}
                                        onChange={handleDialogConditionChange}
                                        disabled={loading}
                                        rulesSchema={rulesSchema}
                                    />
                                ) : conditionDialog.condition?.conditionType === 'VOTE' ? (
                                    <VoteConditionBuilder
                                        condition={conditionDialog.condition}
                                        onChange={handleDialogConditionChange}
                                        disabled={loading}
                                        rulesSchema={rulesSchema}
                                        indicatorSchemas={indicatorSchemas}
                                    />
                                ) : conditionDialog.condition?.conditionType === 'CHAIN' ? (
                                    <ChainConditionBuilder
                                        condition={conditionDialog.condition}
                                        onChange={handleDialogConditionChange}
                                        disabled={loading}
                                        rulesSchema={rulesSchema}
                                        indicatorSchemas={indicatorSchemas}
                                    />
                                ) : conditionDialog.condition ? (
                                    <TrendLineConditionBuilder
                                        condition={conditionDialog.condition}
                                        onChange={handleDialogConditionChange}
                                        errors={{}}
                                        disabled={loading}
                                        availableTrendLines={availableTrendLines}
                                        rulesSchema={rulesSchema}
                                        tickerSymbol={formData?.tickerName}
                                    />
                                ) : null}
                            </>
                        )}
                    </div>

                    {conditionDialog.view === 'BUILD' && (
                        <div className="modal-action flex justify-between items-center w-full">
                            <div>
                                {(conditionDialog.condition?.conditionType === 'INDICATOR' || conditionDialog.condition?.conditionType === 'COMPOSITE') && (
                                    <button
                                        className="btn btn-outline btn-sm gap-2"
                                        onClick={handleSaveToLibraryClick}
                                        disabled={loading || !conditionDialog.condition}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                                        </svg>
                                        Save to Library
                                    </button>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <button className="btn btn-ghost" onClick={closeConditionDialog}>Cancel</button>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSaveCondition}
                                    disabled={loading}
                                >
                                    {conditionDialog.conditionIndex === null ? 'Add Condition' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={closeConditionDialog}>close</button>
                </form>
            </dialog>

            {/* TrendLine Modal */}
            <TrendLineChartModal
                open={trendLineModal.open}
                onClose={() => setTrendLineModal({ ...trendLineModal, open: false })}
                symbol={formData.tickerName || 'TITAN'}
                onSelectTrendLine={(line) => {
                    handleTrendLineSelection(line);
                    setTrendLineModal({ ...trendLineModal, open: false });
                }}
            />
        </div >
    );
};

export default StrategyBuilder;
